/**
 * Mission 35: Admin Campaign Editor
 *
 * 60/40 split: form left, live preview right.
 * Sections: Basics, Steps, Trigger Config, Content, Preview, Targeting, Scheduling, Actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { AdminCampaignService } from '../services/adminCampaignService';
import {
  type StylePreset,
  getPresetsForType,
  applyPresetToDisplayConfig,
} from './campaignPresets';
// Shared campaign icon system (single source of truth). The web preview
// uses the lucide-react variant; the real native modal uses SF Symbols on
// iOS. Pinned by tests/gate/test_campaigns_design_studio.py.
import { CampaignIcon, CedarTeamBadge } from '../components/campaigns/campaignIcons';

const TYPE_OPTIONS = [
  { value: 'announcement', label: 'Announcement', desc: 'Full-screen takeover or carousel for major news' },
  { value: 'contextual_notice', label: 'Contextual Notice', desc: 'Soft bottom sheet for coming-soon or disabled features' },
  { value: 'cedar_chat', label: 'Cedar Chat Message', desc: 'Message from Cedar in the chat feed (looks like normal coaching)' },
  { value: 'survey', label: 'Survey', desc: 'Team-branded survey (native only, 1-3 questions, consent modal first)' },
];

const FREQUENCY_OPTIONS = [
  { value: 'once_ever', label: 'Once ever' },
  { value: 'once_per_session', label: 'Once per session' },
  { value: 'every_trigger', label: 'Every time trigger fires' },
  { value: 'n_times', label: 'N times total' },
  // Phase B (2026-05-21): recurring-while-eligible mode.
  // Delivers at most once per user-local day; auto-stops when user
  // exits the targeting segment; capped at max_lifetime_impressions
  // (REQUIRED — admin UI + status_change both enforce).
  { value: 'until_segment_exits', label: 'Until user exits segment (daily)' },
];

const QUESTION_TYPES = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'free_text', label: 'Free Text' },
  { value: 'rating', label: 'Rating Scale' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
];

// ================================================================
// Style Presets — sourced from the canonical ./campaignPresets module.
// StylePreset, getPresetsForType, and applyPresetToDisplayConfig are
// imported at the top of this file. The editor must NOT redefine presets
// inline: a local copy silently diverges from the design-contract file.
//
// Regression fixed 2026-05-27: an inline ANNOUNCEMENT_PRESETS (Cedar Bold,
// Celebration) + local getPresetsForType shadowed the canonical Cedar
// Gradient / aligned palette, so /admin/campaigns/new rendered stale presets
// in prod even though campaignPresets.ts (and the native components) were
// correct. Pinned by tests/gate/test_campaigns_design_studio.py::
// test_editor_uses_canonical_presets_not_inline_duplicate.
// ================================================================

// Phase D (2026-05-21): one A/B variant of a step. `id` is set after
// the variant has been saved at least once so the backend merge UPDATEs
// in place (preserves campaign_impressions.variant_id FK).
interface VariantData {
  id?: string;
  variant_key: string;
  weight: number;
  content: Record<string, any>;
  push_title: string;
  push_body: string;
  // Inline analytics (populated by the parent component when impression
  // data is loaded). Read-only in the editor.
  impressions?: number;
  cta_taps?: number;
  ctr?: number;
}

interface StepData {
  id?: string;  // Populated when editing existing campaign (needed for manual fire)
  trigger_type: string;
  trigger_ids: string[];
  trigger_config: Record<string, any>;
  requires_previous_step: boolean;
  message_type: string;
  content: Record<string, any>;
  display_config: Record<string, any>;
  send_push: boolean;
  push_title: string;
  push_body: string;
  // Phase C (2026-05-21): per-step scheduling overrides. All optional —
  // null/empty = disabled (pre-Phase-C behavior). See migration 1547
  // and services/campaign_delivery_service.py:_check_eligibility for
  // semantics. Backend Pydantic StepCreate enforces HH:MM format +
  // both-or-neither pairing on quiet_hours; UI mirrors with hints.
  delay_after_previous_minutes: number | null;
  send_at_local_time: string | null;
  send_window_minutes: number | null;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  // Phase D (2026-05-21): A/B variants. Empty array = single content
  // (uses step.content / push_title / push_body — pre-Phase-D behavior).
  // 2+ variants = active A/B test. 1 is rejected by Pydantic.
  variants: VariantData[];
}

// Phase D variant_key character set — mirrors routers/admin_campaigns.py:_VARIANT_KEY_RE.
const VARIANT_KEY_RE = /^[A-Za-z0-9 _.\-]{1,50}$/;

// HH:MM (24h). Mirrored from routers/admin_campaigns.py:_HHMM_RE.
// UI uses this to surface a per-field hint before the round-trip to
// the API, so admins don't see a generic 422.
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const AdminCampaignEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  // Campaign state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [campaignType, setCampaignType] = useState('announcement');
  const [frequency, setFrequency] = useState('once_ever');
  const [maxImpressions, setMaxImpressions] = useState(3);
  // Phase B (2026-05-21): until_segment_exits lifetime cap. NULL when
  // not using this frequency; REQUIRED when frequency=until_segment_exits.
  const [maxLifetimeImpressions, setMaxLifetimeImpressions] = useState<number | null>(null);
  const [isMultiStep, setIsMultiStep] = useState(false);
  const [surveyCooldownDays, setSurveyCooldownDays] = useState(14);
  // Phase A.1 (2026-05-21): per-campaign opt-in to respecting user
  // nudge_pref.enabled before delivering push. Default FALSE preserves
  // the pre-A.1 mandatory-push behavior of every existing campaign.
  const [respectNotifPrefs, setRespectNotifPrefs] = useState(false);
  const [status, setStatus] = useState('draft');

  // Steps
  const [steps, setSteps] = useState<StepData[]>([createEmptyStep('announcement')]);

  // Triggers registry
  const [availableTriggers, setAvailableTriggers] = useState<any[]>([]);

  // Targeting
  const [targetingMode, setTargetingMode] = useState<'all' | 'segment' | 'custom' | 'specific_users'>('all');
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [customFilters, setCustomFilters] = useState<Array<{ field: string; operator: string; value: any }>>([]);
  const [excludeFilters, setExcludeFilters] = useState<Array<{ field: string; operator: string; value: any }>>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [audienceTotal, setAudienceTotal] = useState<number | null>(null);
  const [savingSegment, setSavingSegment] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState('');
  // Specific users picker
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; email: string; firstName: string; lastName: string }>>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Scheduling
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [timezoneMode, setTimezoneMode] = useState('user');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [firingStepIdx, setFiringStepIdx] = useState<number | null>(null);
  const [fireResult, setFireResult] = useState<{ idx: number; delivered: number; skipped: number } | null>(null);

  const handleFireStep = async (idx: number) => {
    const step = steps[idx];
    if (!id || !step.id) return;
    setFiringStepIdx(idx);
    setFireResult(null);
    try {
      const result = await AdminCampaignService.fireManualStep(id, step.id);
      setFireResult({ idx, delivered: result.delivered || 0, skipped: result.skipped || 0 });
    } catch (e: any) {
      setError(`Failed to fire step ${idx + 1}: ${e.message}`);
    } finally {
      setFiringStepIdx(null);
    }
  };

  // Load triggers and segments
  useEffect(() => {
    AdminCampaignService.getTriggers()
      .then(setAvailableTriggers)
      .catch((e) => console.error('Failed to load triggers:', e));
    AdminCampaignService.listSegments()
      .then(setSegments)
      .catch((e) => console.error('Failed to load segments:', e));
  }, []);

  // Load active users when specific_users mode selected
  useEffect(() => {
    if (targetingMode === 'specific_users' && !usersLoaded && !usersLoading) {
      setUsersLoading(true);
      AdminCampaignService.getActiveUsers()
        .then((users) => { setAvailableUsers(users); setUsersLoaded(true); })
        .catch((e) => { console.error('Failed to load users:', e); setError('Failed to load users'); setUsersLoaded(true); })
        .finally(() => setUsersLoading(false));
    }
  }, [targetingMode, usersLoaded]);

  // Update audience count when filters change
  useEffect(() => {
    if (targetingMode === 'specific_users') {
      setAudienceCount(selectedUserIds.length > 0 ? selectedUserIds.length : null);
      setAudienceTotal(null);
      return;
    }
    const filters = targetingMode === 'custom' ? customFilters : [];
    if (targetingMode === 'all' || filters.length === 0) {
      setAudienceCount(null);
      return;
    }
    const timer = setTimeout(() => {
      AdminCampaignService.getAudienceCount(filters)
        .then((data) => { setAudienceCount(data.count); setAudienceTotal(data.total_users); })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [customFilters, targetingMode, selectedUserIds]);

  // Load existing campaign
  useEffect(() => {
    if (!isNew && id) {
      AdminCampaignService.getCampaign(id).then((data) => {
        setName(data.name || '');
        setDescription(data.description || '');
        setCampaignType(data.campaign_type || 'announcement');
        setFrequency(data.frequency || 'once_ever');
        setMaxImpressions(data.max_impressions || 3);
        setMaxLifetimeImpressions(data.max_lifetime_impressions ?? null);
        setIsMultiStep(data.is_multi_step || false);
        setRespectNotifPrefs(!!data.respect_notification_preferences);
        setSurveyCooldownDays(data.survey_cooldown_days || 14);
        setStatus(data.status || 'draft');
        setStartsAt(data.starts_at || '');
        setEndsAt(data.ends_at || '');
        setTimezoneMode(data.timezone_mode || 'user');
        if (data.targeting) {
          if (data.targeting.segment_id) {
            setTargetingMode('segment');
            setSelectedSegmentId(data.targeting.segment_id);
          } else if (data.targeting.custom_filters) {
            const cf = Array.isArray(data.targeting.custom_filters)
              ? data.targeting.custom_filters
              : (typeof data.targeting.custom_filters === 'string' ? JSON.parse(data.targeting.custom_filters) : []);
            const specificFilter = cf.find(
              (f: any) => f.field === 'specific_users'
            );
            if (specificFilter && cf.length === 1) {
              setTargetingMode('specific_users');
              setSelectedUserIds(specificFilter.value || []);
            } else {
              setTargetingMode('custom');
              setCustomFilters(cf);
            }
          }
          if (data.targeting.exclude_filters) {
            const ef = Array.isArray(data.targeting.exclude_filters)
              ? data.targeting.exclude_filters
              : (typeof data.targeting.exclude_filters === 'string' ? JSON.parse(data.targeting.exclude_filters) : []);
            setExcludeFilters(ef);
          }
        }
        if (data.steps?.length > 0) {
          setSteps(data.steps.map((s: any) => ({
            id: s.id || undefined,
            trigger_type: s.trigger_type || 'event',
            trigger_ids: s.trigger_ids || [],
            trigger_config: s.trigger_config || {},
            requires_previous_step: s.requires_previous_step || false,
            message_type: s.message_type || data.campaign_type,
            content: s.content || {},
            display_config: s.display_config || {},
            send_push: s.send_push || false,
            push_title: s.push_title || '',
            push_body: s.push_body || '',
            // Phase C: hydrate per-step scheduling overrides. `?? null`
            // (not `|| null`) so a stored 0 stays 0 — though Pydantic
            // rejects 0, so this is mostly defensive against future
            // changes that might allow it.
            delay_after_previous_minutes: s.delay_after_previous_minutes ?? null,
            send_at_local_time: s.send_at_local_time ?? null,
            send_window_minutes: s.send_window_minutes ?? null,
            quiet_hours_start: s.quiet_hours_start ?? null,
            quiet_hours_end: s.quiet_hours_end ?? null,
            // Phase D: hydrate A/B variants. Backend returns sorted by
            // variant_key (deterministic-assignment guarantee). Coerce
            // null fields to empty strings since the editor inputs
            // are <input> not nullable.
            variants: (s.variants || []).map((v: any) => ({
              id: v.id || undefined,
              variant_key: v.variant_key || '',
              weight: Number(v.weight) || 1,
              content: v.content || {},
              push_title: v.push_title || '',
              push_body: v.push_body || '',
            })),
          })));
        }
      }).catch((e) => {
        console.error('Failed to load campaign:', e);
        setError('Failed to load campaign');
      });
    }
  }, [id, isNew]);

  // Phase B (2026-05-21): until_segment_exits frequency requires a
  // lifetime cap. Mirror the backend check (services/campaign_service.py
  // change_status) so the UI doesn't let admins try to activate a
  // misconfigured campaign and get a 422 back.
  const needsLifetimeCap = frequency === 'until_segment_exits' && !maxLifetimeImpressions;

  // Phase C (2026-05-21): mirror the backend Pydantic StepCreate
  // validators so the activation button is disabled before the round-trip.
  // Catches: (a) HH:MM strings that don't match ^([01]\d|2[0-3]):[0-5]\d$,
  // (b) only one of quiet_hours start/end set, (c) equal quiet_hours pair
  // (empty window). Pydantic would also reject these, but we want a clean
  // local hint instead of a generic 422.
  const stepsHaveBadHHMM = steps.some((s) => (
    (s.send_at_local_time !== null && !HHMM_RE.test(s.send_at_local_time))
    || (s.quiet_hours_start !== null && !HHMM_RE.test(s.quiet_hours_start))
    || (s.quiet_hours_end !== null && !HHMM_RE.test(s.quiet_hours_end))
  ));
  const stepsHaveLonelyQuietHours = steps.some((s) => (
    (s.quiet_hours_start === null) !== (s.quiet_hours_end === null)
  ));
  const stepsHaveEqualQuietHours = steps.some((s) => (
    s.quiet_hours_start !== null
    && s.quiet_hours_end !== null
    && s.quiet_hours_start === s.quiet_hours_end
  ));
  const stepsHaveBadScheduling = stepsHaveBadHHMM || stepsHaveLonelyQuietHours || stepsHaveEqualQuietHours;

  // Phase D (2026-05-21): mirror the backend variant validators so the
  // Activate button reflects local validity before the round-trip.
  // - Exactly 1 variant on any step → Pydantic 422 (single_variant_disallowed)
  // - Duplicate variant_key within a step → Pydantic 422 (duplicate within seen set)
  // - Bad variant_key format → Pydantic 422 (regex mismatch)
  // - weight <= 0 → Pydantic 422
  const stepsHaveSingleVariant = steps.some((s) => s.variants.length === 1);
  const stepsHaveBadVariantKey = steps.some((s) =>
    s.variants.some((v) => !VARIANT_KEY_RE.test(v.variant_key.trim()))
  );
  const stepsHaveDupVariantKey = steps.some((s) => {
    const keys = s.variants.map((v) => v.variant_key.trim());
    return new Set(keys).size !== keys.length;
  });
  const stepsHaveBadVariantWeight = steps.some((s) =>
    s.variants.some((v) => !Number.isFinite(v.weight) || v.weight <= 0)
  );
  const stepsHaveBadVariants = stepsHaveSingleVariant
    || stepsHaveBadVariantKey
    || stepsHaveDupVariantKey
    || stepsHaveBadVariantWeight;

  const canActivate = steps.length > 0
    && steps.some((s) => s.trigger_type)
    && !needsLifetimeCap
    && !stepsHaveBadScheduling
    && !stepsHaveBadVariants;
  const activationBlockedReason = !canActivate
    ? (steps.length === 0 || !steps.some((s) => s.trigger_type)
        ? 'Add at least one step with a trigger before activating'
        : needsLifetimeCap
          ? 'Set Max lifetime impressions before activating (required for "Until user exits segment")'
          : stepsHaveBadHHMM
            ? 'One or more scheduling-override times are not valid HH:MM (24h)'
            : stepsHaveLonelyQuietHours
              ? 'Quiet hours start and end must both be set or both blank'
              : stepsHaveEqualQuietHours
                ? 'Quiet hours start and end must differ (an empty window is meaningless)'
                : stepsHaveSingleVariant
                  ? 'A step must have either zero variants or at least two — remove or add a second variant'
                  : stepsHaveBadVariantKey
                    ? 'Variant keys must be 1-50 chars of letters, digits, space, _ . or -'
                    : stepsHaveDupVariantKey
                      ? 'Variant keys must be unique within a step'
                      : stepsHaveBadVariantWeight
                        ? 'Variant weights must be positive integers'
                        : undefined)
    : undefined;

  const handleSave = useCallback(async (activate = false) => {
    if (activate && steps.length === 0) {
      setError('Cannot activate a campaign with no steps. Add at least one step before activating.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Targeting: `null` is semantically meaningful to the backend — it means
      // "clear this row" (switch back to All users). `undefined` would be
      // stripped by JSON.stringify and the backend would leave the existing
      // targeting row untouched, so we must be explicit.
      const targeting = targetingMode === 'all' ? null : {
        segment_id: targetingMode === 'segment' ? selectedSegmentId : undefined,
        custom_filters: targetingMode === 'specific_users' && selectedUserIds.length > 0
          ? [{ field: 'specific_users', operator: 'in', value: selectedUserIds }]
          : targetingMode === 'custom' && customFilters.length > 0 ? customFilters : undefined,
        exclude_filters: excludeFilters.length > 0 ? excludeFilters : undefined,
      };

      const payload = {
        name,
        description: description || undefined,
        campaign_type: campaignType,
        starts_at: startsAt || undefined,
        ends_at: endsAt || undefined,
        timezone_mode: timezoneMode,
        frequency,
        max_impressions: frequency === 'n_times' ? maxImpressions : undefined,
        // Phase B: lifetime cap only sent when frequency is
        // until_segment_exits. Backend ignores it for other frequencies
        // but staying tidy on the wire saves admin confusion.
        max_lifetime_impressions: frequency === 'until_segment_exits' ? maxLifetimeImpressions : undefined,
        is_multi_step: isMultiStep,
        // Phase A.1 (2026-05-21): opt-in flag for _send_campaign_push to
        // honor user nudge_pref.enabled. Default FALSE matches the
        // pre-A.1 mandatory-push behavior of every existing campaign.
        respect_notification_preferences: respectNotifPrefs,
        survey_cooldown_days: campaignType === 'survey' ? surveyCooldownDays : undefined,
        // Step ids (when present) are preserved so the backend UPDATEs in
        // place instead of re-inserting. This keeps FK linkage from
        // campaign_impressions and pending_campaign_deliveries intact.
        steps: steps.map((s) => ({
          ...s,
          message_type: s.message_type || campaignType,
        })),
        targeting,
      };

      let campaignId = id;
      if (isNew) {
        const result = await AdminCampaignService.createCampaign(payload);
        campaignId = result.id;
      } else {
        await AdminCampaignService.updateCampaign(id!, payload);
      }

      if (activate && campaignId) {
        await AdminCampaignService.changeStatus(campaignId, 'active');
      }

      navigate(`/admin/campaigns/${campaignId}`);
    } catch (e: any) {
      setError(e.message || 'Failed to save campaign');
    }
    setSaving(false);
  }, [name, description, campaignType, frequency, maxImpressions, maxLifetimeImpressions, isMultiStep, respectNotifPrefs, surveyCooldownDays, steps, id, isNew, navigate, startsAt, endsAt, timezoneMode, targetingMode, selectedSegmentId, customFilters, excludeFilters, selectedUserIds]);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!id) return;
    try {
      await AdminCampaignService.changeStatus(id, newStatus);
      setStatus(newStatus);
    } catch (e: any) {
      setError(e.message);
    }
  }, [id]);

  const updateStep = (index: number, field: string, value: any) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const updateStepContent = (index: number, field: string, value: any) => {
    setSteps((prev) => prev.map((s, i) =>
      i === index ? { ...s, content: { ...s.content, [field]: value } } : s,
    ));
  };

  const updateStepDisplayConfig = (index: number, field: string, value: any) => {
    setSteps((prev) => prev.map((s, i) =>
      i === index ? { ...s, display_config: { ...s.display_config, [field]: value } } : s,
    ));
  };

  const [openDisplayConfig, setOpenDisplayConfig] = useState<Record<number, boolean>>({});
  // Phase C (2026-05-21): per-step "Scheduling overrides" accordion.
  // Default collapsed so the Step card stays compact; admins opt in
  // when they actually need per-step scheduling control.
  const [openScheduling, setOpenScheduling] = useState<Record<number, boolean>>({});
  // Phase D (2026-05-21): per-step "A/B variants" accordion. Default
  // collapsed for the same reason — most campaigns ship a single
  // content version.
  const [openVariants, setOpenVariants] = useState<Record<number, boolean>>({});

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <button onClick={() => navigate('/admin/campaigns')} style={backLinkStyle}>← Back to Campaigns</button>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: '8px 0 0' }}>
            {isNew ? 'New Campaign' : name || 'Edit Campaign'}
          </h1>
          {!isNew && (
            <span style={{
              padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600,
              background: STATUS_COLORS[status]?.bg || '#F3F4F6',
              color: STATUS_COLORS[status]?.text || '#374151',
              marginLeft: 12,
            }}>
              {status}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* 60/40 Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 24 }}>
        {/* LEFT: Form */}
        <div>
          {/* Section 1: Basics */}
          <Section title="Campaign Basics">
            <FormField label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Campaign name" />
            </FormField>
            <FormField label="Description">
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: 60 }} placeholder="Optional description" />
            </FormField>
            <FormField label="Type">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {TYPE_OPTIONS.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => {
                      setCampaignType(t.value);
                      setSteps((prev) => prev.map((s) => ({ ...s, message_type: t.value, content: {} })));
                    }}
                    style={{
                      padding: 12, borderRadius: 10, border: '2px solid',
                      borderColor: campaignType === t.value ? '#1F6C4F' : '#E5E7EB',
                      background: campaignType === t.value ? '#F0FDF4' : '#fff',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </FormField>
          </Section>

          {/* Section 2: Steps */}
          <Section title={`Steps (${steps.length})`}>
            {steps.map((step, idx) => (
              <div key={idx} style={{ padding: 16, border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Step {idx + 1}</div>

                {/* Trigger */}
                <FormField label="Trigger Type">
                  <select value={step.trigger_type} onChange={(e) => updateStep(idx, 'trigger_type', e.target.value)} style={selectStyle}>
                    <option value="broadcast">Broadcast (all users on next sweep)</option>
                    <option value="event">Event (user action triggers)</option>
                    <option value="time">Time (lifecycle: trial day, account age)</option>
                    <option value="manual">Manual (admin fires)</option>
                  </select>
                </FormField>

                {step.trigger_type === 'manual' && step.id && status === 'active' && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#FEF3C7', borderRadius: 8, border: '1px solid #F59E0B' }}>
                    <div style={{ fontSize: 13, color: '#92400E', marginBottom: 8 }}>
                      This step will fire when you press the button below. It delivers to all eligible users who match targeting
                      {step.requires_previous_step ? ' and saw the previous step' : ''}.
                    </div>
                    <button
                      onClick={() => handleFireStep(idx)}
                      disabled={firingStepIdx === idx}
                      style={{
                        padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
                        fontWeight: 600, fontSize: 14,
                        background: firingStepIdx === idx ? '#D1D5DB' : '#DC2626',
                        color: '#fff',
                      }}
                    >
                      {firingStepIdx === idx ? 'Firing...' : '🚀 Fire Step Now'}
                    </button>
                    {fireResult?.idx === idx && (
                      <div style={{ marginTop: 8, fontSize: 13, color: '#065F46', fontWeight: 600 }}>
                        Delivered to {fireResult.delivered} user{fireResult.delivered !== 1 ? 's' : ''}
                        {fireResult.skipped > 0 && (
                          <span style={{ color: '#6B7280', fontWeight: 500 }}>
                            {' · '}{fireResult.skipped} skipped (not eligible / targeting)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {step.trigger_type === 'manual' && !step.id && (
                  <div style={{ marginBottom: 16, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                    Save the campaign first, then the Fire button will appear here.
                  </div>
                )}

                {step.trigger_type === 'manual' && step.id && status !== 'active' && (
                  <div style={{ marginBottom: 16, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                    Activate the campaign to enable the Fire button.
                  </div>
                )}

                {step.trigger_type === 'event' && (
                  <FormField label="Trigger Points (select multiple)">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {availableTriggers.map((t) => (
                        <button
                          key={t.trigger_id}
                          onClick={() => {
                            const ids = step.trigger_ids.includes(t.trigger_id)
                              ? step.trigger_ids.filter((id: string) => id !== t.trigger_id)
                              : [...step.trigger_ids, t.trigger_id];
                            updateStep(idx, 'trigger_ids', ids);
                          }}
                          title={t.description}
                          style={{
                            padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                            border: '1px solid',
                            borderColor: step.trigger_ids.includes(t.trigger_id) ? '#1F6C4F' : '#D1D5DB',
                            background: step.trigger_ids.includes(t.trigger_id) ? '#1F6C4F' : '#fff',
                            color: step.trigger_ids.includes(t.trigger_id) ? '#fff' : '#374151',
                          }}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </FormField>
                )}

                {idx > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={step.requires_previous_step}
                      onChange={(e) => updateStep(idx, 'requires_previous_step', e.target.checked)}
                    />
                    Only show to users who saw previous step
                  </label>
                )}

                {/* Phase C (2026-05-21): Per-step Scheduling overrides
                    accordion. Mirror of the Design Studio accordion
                    pattern — collapsed by default so the Step card
                    stays compact for the simple cases. Backend Pydantic
                    StepCreate (routers/admin_campaigns.py) validates
                    HH:MM and quiet_hours pairing; the UI surfaces a
                    per-field hint when invalid before round-tripping. */}
                <div style={{ marginTop: 12, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenScheduling((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                    style={{
                      width: '100%', padding: '10px 14px', background: '#F9FAFB', border: 'none',
                      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 13, fontWeight: 600, color: '#374151',
                    }}
                  >
                    <span>
                      Scheduling overrides
                      {(step.delay_after_previous_minutes !== null
                        || step.send_at_local_time !== null
                        || step.quiet_hours_start !== null) && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#1F6C4F', fontWeight: 500 }}>
                          (active)
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{openScheduling[idx] ? '▲' : '▼'}</span>
                  </button>
                  {openScheduling[idx] && (
                    <div style={{ padding: 14, background: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                        All fields optional. Leave blank to use campaign-level defaults.
                        All times are evaluated in the <strong>user's local timezone</strong>.
                      </div>

                      {/* C.1 — Delay after previous step. Only meaningful when requires_previous_step is checked. */}
                      {step.requires_previous_step && (
                        <FormField label="Wait N minutes after previous step lands">
                          <input
                            type="number"
                            min={1}
                            value={step.delay_after_previous_minutes ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateStep(idx, 'delay_after_previous_minutes', v === '' ? null : Number(v));
                            }}
                            style={inputStyle}
                            placeholder="e.g. 4320 (3 days)"
                          />
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                            Blocks dispatch until this many minutes have passed since the user's most recent impression of the previous step. Blank = fire immediately on the next sweep tick after the previous step lands (pre-Phase-C behavior).
                          </div>
                        </FormField>
                      )}

                      {/* C.2 — Send at user-local time + window */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <FormField label="Send at user-local time">
                          <input
                            type="text"
                            value={step.send_at_local_time ?? ''}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              updateStep(idx, 'send_at_local_time', v === '' ? null : v);
                            }}
                            style={{
                              ...inputStyle,
                              borderColor: (step.send_at_local_time && !HHMM_RE.test(step.send_at_local_time))
                                ? '#DC2626' : inputStyle.border?.toString().includes('d1d5db') ? '#d1d5db' : '#D1D5DB',
                            }}
                            placeholder="HH:MM (e.g. 08:00)"
                          />
                          {step.send_at_local_time && !HHMM_RE.test(step.send_at_local_time) && (
                            <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                              Must be HH:MM (24h), e.g. 08:00, 22:30
                            </div>
                          )}
                        </FormField>
                        <FormField label="Window (minutes)">
                          <input
                            type="number"
                            min={1}
                            value={step.send_window_minutes ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateStep(idx, 'send_window_minutes', v === '' ? null : Number(v));
                            }}
                            style={inputStyle}
                            placeholder="60"
                          />
                        </FormField>
                      </div>
                      {step.send_at_local_time && (
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: -8 }}>
                          Step fires only when user-local time is in [{step.send_at_local_time}, {step.send_at_local_time} + {step.send_window_minutes ?? 60}m). Blank window defaults to 60 minutes.
                        </div>
                      )}

                      {/* C.3 — Quiet hours (suppression window) */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <FormField label="Quiet hours start">
                          <input
                            type="text"
                            value={step.quiet_hours_start ?? ''}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              updateStep(idx, 'quiet_hours_start', v === '' ? null : v);
                            }}
                            style={{
                              ...inputStyle,
                              borderColor: (step.quiet_hours_start && !HHMM_RE.test(step.quiet_hours_start))
                                ? '#DC2626' : '#d1d5db',
                            }}
                            placeholder="HH:MM (e.g. 22:00)"
                          />
                          {step.quiet_hours_start && !HHMM_RE.test(step.quiet_hours_start) && (
                            <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                              Must be HH:MM (24h)
                            </div>
                          )}
                        </FormField>
                        <FormField label="Quiet hours end">
                          <input
                            type="text"
                            value={step.quiet_hours_end ?? ''}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              updateStep(idx, 'quiet_hours_end', v === '' ? null : v);
                            }}
                            style={{
                              ...inputStyle,
                              borderColor: (step.quiet_hours_end && !HHMM_RE.test(step.quiet_hours_end))
                                ? '#DC2626' : '#d1d5db',
                            }}
                            placeholder="HH:MM (e.g. 07:00)"
                          />
                          {step.quiet_hours_end && !HHMM_RE.test(step.quiet_hours_end) && (
                            <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                              Must be HH:MM (24h)
                            </div>
                          )}
                        </FormField>
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: -8 }}>
                        Suppress dispatch when user-local time is inside this window.
                        If <strong>end &lt; start</strong> the window wraps midnight (e.g.
                        22:00 → 07:00 means quiet from 22:00 through 06:59). Both fields
                        must be set together, or both blank.
                      </div>
                      {(step.quiet_hours_start === null) !== (step.quiet_hours_end === null) && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: -8 }}>
                          Quiet hours start and end must both be set or both blank.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Phase D (2026-05-21): A/B variants accordion. Same
                    collapsed-by-default pattern as Scheduling overrides.
                    Empty state shows "Add A/B variants" which seeds 2
                    variants (A pre-filled with current step content, B
                    blank) so admins don't start from scratch. Activate
                    button + Pydantic mirror catch invalid configs before
                    round-trip. */}
                <div style={{ marginTop: 12, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenVariants((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                    style={{
                      width: '100%', padding: '10px 14px', background: '#F9FAFB', border: 'none',
                      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 13, fontWeight: 600, color: '#374151',
                    }}
                  >
                    <span>
                      A/B variants
                      {step.variants.length > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#1F6C4F', fontWeight: 500 }}>
                          ({step.variants.length} active)
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{openVariants[idx] ? '▲' : '▼'}</span>
                  </button>
                  {openVariants[idx] && (
                    <div style={{ padding: 14, background: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {step.variants.length === 0 ? (
                        <>
                          <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                            No A/B test active — this step delivers from the single content version above.
                            Add variants to test different copy or push messages, with deterministic per-user
                            assignment so the same user always sees the same variant.
                          </div>
                          <button
                            onClick={() => {
                              const variantA: VariantData = {
                                variant_key: 'A',
                                weight: 50,
                                content: { ...step.content },
                                push_title: step.push_title || '',
                                push_body: step.push_body || '',
                              };
                              const variantB: VariantData = {
                                variant_key: 'B',
                                weight: 50,
                                content: {},
                                push_title: '',
                                push_body: '',
                              };
                              updateStep(idx, 'variants', [variantA, variantB]);
                            }}
                            style={{
                              padding: '10px 16px', borderRadius: 8, border: '1px solid #1F6C4F',
                              background: '#fff', color: '#1F6C4F', fontSize: 13, fontWeight: 600,
                              cursor: 'pointer', alignSelf: 'flex-start',
                            }}
                          >
                            + Add A/B variants (seeds A with current content, B blank, 50/50 weight)
                          </button>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                            Users are deterministically assigned to a variant based on
                            <code style={{ background: '#F3F4F6', padding: '1px 4px', borderRadius: 4 }}>hash(user_id:step_id)</code>.
                            Same user always sees the same variant. Adjusting weights re-balances new assignments
                            but doesn't reshuffle users whose pick falls in the unchanged ranges. Variant push_title /
                            push_body left blank inherit from the step's defaults above.
                          </div>
                          {step.variants.map((variant, vIdx) => {
                            const variantKeyBad = !VARIANT_KEY_RE.test(variant.variant_key.trim());
                            const variantKeyDup = step.variants
                              .filter((_, i) => i !== vIdx)
                              .some((other) => other.variant_key.trim() === variant.variant_key.trim());
                            const weightBad = !Number.isFinite(variant.weight) || variant.weight <= 0;
                            const hasImpressions = (variant.impressions ?? 0) > 0;
                            return (
                              <div
                                key={vIdx}
                                style={{
                                  border: '1px solid #E5E7EB', borderRadius: 8, padding: 12,
                                  background: '#F9FAFB',
                                }}
                              >
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px auto', gap: 12, alignItems: 'flex-end' }}>
                                  <FormField label="Variant key">
                                    <input
                                      value={variant.variant_key}
                                      onChange={(e) => {
                                        const newVariants = [...step.variants];
                                        newVariants[vIdx] = { ...variant, variant_key: e.target.value };
                                        updateStep(idx, 'variants', newVariants);
                                      }}
                                      style={{
                                        ...inputStyle,
                                        borderColor: (variantKeyBad || variantKeyDup) ? '#DC2626' : '#d1d5db',
                                      }}
                                      placeholder="A"
                                    />
                                    {variantKeyBad && (
                                      <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                                        1-50 chars (letters, digits, space, _ . -)
                                      </div>
                                    )}
                                    {!variantKeyBad && variantKeyDup && (
                                      <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                                        Duplicate key in this step
                                      </div>
                                    )}
                                  </FormField>
                                  <FormField label="Weight">
                                    <input
                                      type="number"
                                      min={1}
                                      value={variant.weight}
                                      onChange={(e) => {
                                        const newVariants = [...step.variants];
                                        newVariants[vIdx] = { ...variant, weight: Number(e.target.value) };
                                        updateStep(idx, 'variants', newVariants);
                                      }}
                                      style={{
                                        ...inputStyle,
                                        borderColor: weightBad ? '#DC2626' : '#d1d5db',
                                      }}
                                    />
                                  </FormField>
                                  <button
                                    onClick={() => {
                                      if (hasImpressions) return;  // disabled — backend would 409 anyway
                                      const newVariants = step.variants.filter((_, i) => i !== vIdx);
                                      updateStep(idx, 'variants', newVariants);
                                    }}
                                    disabled={hasImpressions}
                                    title={hasImpressions
                                      ? 'Cannot remove a variant with delivery history — set weight to 1 to deprioritize instead'
                                      : 'Remove this variant'}
                                    style={{
                                      padding: '10px 12px', borderRadius: 8,
                                      border: '1px solid ' + (hasImpressions ? '#E5E7EB' : '#DC2626'),
                                      background: '#fff',
                                      color: hasImpressions ? '#9CA3AF' : '#DC2626',
                                      cursor: hasImpressions ? 'not-allowed' : 'pointer',
                                      fontSize: 12,
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div style={{ marginTop: 10 }}>
                                  <FormField label="Variant title">
                                    <input
                                      value={variant.content.title || ''}
                                      onChange={(e) => {
                                        const newVariants = [...step.variants];
                                        newVariants[vIdx] = { ...variant, content: { ...variant.content, title: e.target.value } };
                                        updateStep(idx, 'variants', newVariants);
                                      }}
                                      style={inputStyle}
                                    />
                                  </FormField>
                                  <FormField label={campaignType === 'cedar_chat' ? 'Variant message' : 'Variant body'}>
                                    <textarea
                                      value={variant.content.body || variant.content.message || ''}
                                      onChange={(e) => {
                                        const newVariants = [...step.variants];
                                        const field = campaignType === 'cedar_chat' ? 'message' : 'body';
                                        newVariants[vIdx] = { ...variant, content: { ...variant.content, [field]: e.target.value } };
                                        updateStep(idx, 'variants', newVariants);
                                      }}
                                      style={{ ...inputStyle, minHeight: 60 }}
                                    />
                                  </FormField>
                                  {step.send_push && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                      <FormField label="Push title override (optional)">
                                        <input
                                          value={variant.push_title}
                                          onChange={(e) => {
                                            const newVariants = [...step.variants];
                                            newVariants[vIdx] = { ...variant, push_title: e.target.value };
                                            updateStep(idx, 'variants', newVariants);
                                          }}
                                          style={inputStyle}
                                          placeholder={step.push_title || 'inherits from step'}
                                        />
                                      </FormField>
                                      <FormField label="Push body override (optional)">
                                        <input
                                          value={variant.push_body}
                                          onChange={(e) => {
                                            const newVariants = [...step.variants];
                                            newVariants[vIdx] = { ...variant, push_body: e.target.value };
                                            updateStep(idx, 'variants', newVariants);
                                          }}
                                          style={inputStyle}
                                          placeholder={step.push_body || 'inherits from step'}
                                        />
                                      </FormField>
                                    </div>
                                  )}
                                </div>
                                {(variant.impressions ?? 0) > 0 && (
                                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#fff', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, color: '#374151', display: 'flex', gap: 16 }}>
                                    <span><strong>{variant.impressions}</strong> impressions</span>
                                    <span><strong>{variant.cta_taps ?? 0}</strong> CTA taps</span>
                                    <span><strong>{((variant.ctr ?? 0) * 100).toFixed(1)}%</strong> CTR</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => {
                                // Find next unused key from A, B, C, ...
                                const usedKeys = new Set(step.variants.map((v) => v.variant_key.trim()));
                                let nextKey = '';
                                for (let i = 0; i < 26; i++) {
                                  const c = String.fromCharCode(65 + i);
                                  if (!usedKeys.has(c)) { nextKey = c; break; }
                                }
                                const newVariant: VariantData = {
                                  variant_key: nextKey || `V${step.variants.length + 1}`,
                                  weight: 50,
                                  content: {},
                                  push_title: '',
                                  push_body: '',
                                };
                                updateStep(idx, 'variants', [...step.variants, newVariant]);
                              }}
                              style={{
                                padding: '8px 14px', borderRadius: 8, border: '1px solid #1F6C4F',
                                background: '#fff', color: '#1F6C4F', fontSize: 12, fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              + Add variant
                            </button>
                            <button
                              onClick={() => {
                                const anyHasImpressions = step.variants.some((v) => (v.impressions ?? 0) > 0);
                                if (anyHasImpressions) {
                                  // Backend will 409 — show the same hint inline before the round-trip.
                                  setError('Cannot remove A/B testing on a step with delivery history. Duplicate the campaign to start fresh.');
                                  return;
                                }
                                updateStep(idx, 'variants', []);
                              }}
                              style={{
                                padding: '8px 14px', borderRadius: 8, border: '1px solid #D1D5DB',
                                background: '#fff', color: '#6B7280', fontSize: 12,
                                cursor: 'pointer',
                              }}
                            >
                              Remove A/B testing
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <FormField label="Title">
                  <input value={step.content.title || ''} onChange={(e) => updateStepContent(idx, 'title', e.target.value)} style={inputStyle} />
                </FormField>
                <FormField label={campaignType === 'cedar_chat' ? 'Message (write in Cedar\'s voice)' : 'Body'}>
                  <textarea
                    value={step.content.body || step.content.message || ''}
                    onChange={(e) => updateStepContent(idx, campaignType === 'cedar_chat' ? 'message' : 'body', e.target.value)}
                    style={{ ...inputStyle, minHeight: 80 }}
                  />
                </FormField>

                {/* Survey questions */}
                {campaignType === 'survey' && (
                  <SurveyQuestionsEditor
                    questions={step.content.questions || []}
                    onChange={(q) => updateStepContent(idx, 'questions', q)}
                    thankYouText={step.content.thank_you_text || ''}
                    onThankYouChange={(t) => updateStepContent(idx, 'thank_you_text', t)}
                    introText={step.content.intro_text || ''}
                    onIntroChange={(t) => updateStepContent(idx, 'intro_text', t)}
                  />
                )}

                {/* Push notification */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginTop: 12 }}>
                  <input type="checkbox" checked={step.send_push} onChange={(e) => updateStep(idx, 'send_push', e.target.checked)} />
                  Send push notification
                </label>
                {step.send_push && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                    <input value={step.push_title} onChange={(e) => updateStep(idx, 'push_title', e.target.value)} style={inputStyle} placeholder="Push title" />
                    <input value={step.push_body} onChange={(e) => updateStep(idx, 'push_body', e.target.value)} style={inputStyle} placeholder="Push body" />
                  </div>
                )}

                {/* Icon & CTA (announcement / contextual_notice) */}
                {(campaignType === 'announcement' || campaignType === 'contextual_notice') && (
                  <>
                    <FormField label="Icon">
                      <select value={step.content.icon || ''} onChange={(e) => updateStepContent(idx, 'icon', e.target.value)} style={selectStyle}>
                        <option value="">Default (megaphone)</option>
                        <option value="watch">Watch</option>
                        <option value="rest">Rest</option>
                        <option value="fire">Fire</option>
                        <option value="star">Star</option>
                        <option value="clock">Clock</option>
                        <option value="check">Check</option>
                        <option value="gift">Gift</option>
                        <option value="megaphone">Megaphone</option>
                        <option value="info">Info</option>
                        <option value="bell">Bell</option>
                        <option value="cc">Coach Cedar (CC)</option>
                      </select>
                    </FormField>
                    <FormField label="CTA Button Label">
                      <input
                        value={step.content.cta_primary?.label || ''}
                        onChange={(e) => updateStepContent(idx, 'cta_primary', { ...step.content.cta_primary, label: e.target.value })}
                        style={inputStyle}
                        placeholder="e.g. Learn More"
                      />
                    </FormField>
                    <FormField label="CTA Link URL (optional)">
                      <input
                        value={step.content.cta_primary?.deep_link ?? step.content.cta_primary?.action ?? ''}
                        onChange={(e) => {
                          // Write the canonical `deep_link` the runtime modal
                          // reads; drop the legacy `action` field (the modal
                          // never read it, so links saved there went nowhere).
                          const next = { ...(step.content.cta_primary || {}), deep_link: e.target.value } as any;
                          delete next.action;
                          updateStepContent(idx, 'cta_primary', next);
                        }}
                        style={inputStyle}
                        placeholder="e.g. coachcedar.com/blog or /my-path"
                      />
                    </FormField>
                    <label
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                        color: '#374151', cursor: 'pointer', marginTop: 8,
                        padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: 8,
                        background: (step.display_config?.dismiss_behavior === 'cta_only') ? '#FFFBEB' : '#FFFFFF',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={step.display_config?.dismiss_behavior === 'cta_only'}
                        onChange={(e) =>
                          updateStepDisplayConfig(idx, 'dismiss_behavior', e.target.checked ? 'cta_only' : 'x_and_cta')
                        }
                      />
                      <span style={{ flex: 1 }}>
                        Require CTA press (hide the <strong>×</strong> close button)
                      </span>
                    </label>
                    <SlidesEditor
                      template={step.display_config?.template || 'takeover'}
                      slides={step.content.slides || []}
                      primary={{
                        title: step.content.title || '',
                        body: step.content.body || '',
                        icon: step.content.icon || '',
                      }}
                      onChange={(slides) => updateStepContent(idx, 'slides', slides)}
                    />
                  </>
                )}

                {/* Design Studio (collapsible) */}
                <div style={{ marginTop: 16, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenDisplayConfig((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                    style={{
                      width: '100%', padding: '10px 14px', background: '#F9FAFB', border: 'none',
                      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 13, fontWeight: 600, color: '#374151',
                    }}
                  >
                    <span>Design Studio</span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{openDisplayConfig[idx] ? '▲' : '▼'}</span>
                  </button>
                  {openDisplayConfig[idx] && (
                    <DesignStudio
                      campaignType={campaignType}
                      stepIndex={idx}
                      displayConfig={step.display_config}
                      onUpdate={(field: string, value: any) => updateStepDisplayConfig(idx, field, value)}
                      onApplyPreset={(preset: StylePreset) => {
                        setSteps((prev) => prev.map((s, i) =>
                          i === idx
                            ? { ...s, display_config: applyPresetToDisplayConfig(s.display_config, preset, campaignType) }
                            : s,
                        ));
                      }}
                    />
                  )}
                </div>
              </div>
            ))}

            {isMultiStep && (
              <button
                onClick={() => setSteps([...steps, createEmptyStep(campaignType)])}
                style={{ ...inputStyle, cursor: 'pointer', textAlign: 'center', color: '#1F6C4F', fontWeight: 600 }}
              >
                + Add Step
              </button>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginTop: 12 }}>
              <input type="checkbox" checked={isMultiStep} onChange={(e) => setIsMultiStep(e.target.checked)} />
              Multi-step campaign
            </label>
          </Section>

          {/* Section 3: Targeting */}
          <Section
            title="Targeting"
            collapsible
            defaultOpen={false}
            summary={
              targetingMode === 'all' ? 'All users'
              : targetingMode === 'segment' ? (selectedSegmentId ? 'Saved segment' : 'Segment — none selected')
              : targetingMode === 'specific_users' ? `${selectedUserIds.length} specific user${selectedUserIds.length === 1 ? '' : 's'}`
              : `${customFilters.length} custom filter${customFilters.length === 1 ? '' : 's'}`
            }
          >
            <FormField label="Audience">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['all', 'segment', 'custom', 'specific_users'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setTargetingMode(m)}
                    style={{
                      padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      border: '1.5px solid', borderColor: targetingMode === m ? '#1F6C4F' : '#D1D5DB',
                      background: targetingMode === m ? '#1F6C4F' : '#fff',
                      color: targetingMode === m ? '#fff' : '#374151',
                    }}
                  >
                    {m === 'all' ? 'All Users' : m === 'segment' ? 'Saved Segment' : m === 'custom' ? 'Custom Filters' : 'Specific Users'}
                  </button>
                ))}
              </div>
            </FormField>

            {targetingMode === 'segment' && (
              <FormField label="Segment">
                <select value={selectedSegmentId || ''} onChange={(e) => setSelectedSegmentId(e.target.value || null)} style={selectStyle}>
                  <option value="">Select a segment...</option>
                  {segments.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </FormField>
            )}

            {targetingMode === 'custom' && (
              <>
                <FilterBuilder
                  filters={customFilters}
                  onChange={setCustomFilters}
                  label="Include filters (AND logic)"
                />
                <FilterBuilder
                  filters={excludeFilters}
                  onChange={setExcludeFilters}
                  label="Exclude filters"
                />
                {audienceCount !== null && (
                  <div style={{ padding: 12, background: '#F0FDF4', borderRadius: 8, fontSize: 14, color: '#065F46', fontWeight: 600, marginTop: 8 }}>
                    ~{audienceCount.toLocaleString()} users match {audienceTotal ? `(of ${audienceTotal.toLocaleString()} total)` : ''}
                  </div>
                )}
                {customFilters.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    <input value={newSegmentName} onChange={(e) => setNewSegmentName(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Segment name" />
                    <button
                      onClick={async () => {
                        if (!newSegmentName.trim()) return;
                        setSavingSegment(true);
                        try {
                          const result = await AdminCampaignService.createSegment({ name: newSegmentName, filters: customFilters });
                          setSegments([...segments, { id: result.id, name: newSegmentName }]);
                          setNewSegmentName('');
                        } catch (e) { console.error('Failed to save segment:', e); }
                        setSavingSegment(false);
                      }}
                      disabled={savingSegment || !newSegmentName.trim()}
                      style={{ ...ctaSecondaryStyle, padding: '10px 16px', fontSize: 13, opacity: savingSegment ? 0.6 : 1 }}
                    >
                      {savingSegment ? 'Saving...' : 'Save as Segment'}
                    </button>
                  </div>
                )}
              </>
            )}

            {targetingMode === 'specific_users' && (
              <>
                <div style={{ marginBottom: 8 }}>
                  <input
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    style={inputStyle}
                  />
                </div>
                {usersLoading ? (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading users...</div>
                ) : availableUsers.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No active users found</div>
                ) : (
                  <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    {availableUsers
                      .filter((u) => {
                        const q = userSearchQuery.toLowerCase();
                        return !q || u.email.toLowerCase().includes(q)
                          || (u.firstName || '').toLowerCase().includes(q)
                          || (u.lastName || '').toLowerCase().includes(q);
                      })
                      .map((u) => (
                        <label
                          key={u.id}
                          style={{
                            display: 'flex', alignItems: 'center', padding: '8px 12px',
                            borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                            background: selectedUserIds.includes(u.id) ? '#F0FDF4' : 'transparent',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.id]);
                              else setSelectedUserIds(selectedUserIds.filter((id) => id !== u.id));
                            }}
                            style={{ marginRight: 10, accentColor: '#1F6C4F' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                              {u.firstName} {u.lastName}
                            </div>
                            <div style={{ fontSize: 11, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {u.email}
                            </div>
                          </div>
                        </label>
                      ))
                    }
                  </div>
                )}
                {selectedUserIds.length > 0 && (
                  <div style={{ marginTop: 8, padding: 12, background: '#F0FDF4', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#065F46' }}>
                      {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                    </div>
                    <button
                      onClick={() => setSelectedUserIds([])}
                      style={{ fontSize: 12, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </>
            )}

          </Section>

          {/* Section 4: Scheduling */}
          <Section
            title="Scheduling & Frequency"
            collapsible
            defaultOpen={false}
            summary={`${FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.label || frequency}${startsAt ? ' · scheduled' : ' · immediate'}${endsAt ? ' · has end' : ''}`}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <FormField label="Starts at">
                <input type="datetime-local" value={startsAt ? startsAt.slice(0, 16) : ''} onChange={(e) => setStartsAt(e.target.value ? new Date(e.target.value).toISOString() : '')} style={inputStyle} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Leave blank for immediate on activate</div>
              </FormField>
              <FormField label="Ends at">
                <input type="datetime-local" value={endsAt ? endsAt.slice(0, 16) : ''} onChange={(e) => setEndsAt(e.target.value ? new Date(e.target.value).toISOString() : '')} style={inputStyle} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Leave blank for no expiration</div>
              </FormField>
            </div>
            <FormField label="Timezone">
              <div style={{ display: 'flex', gap: 8 }}>
                {(['user', 'utc'] as const).map((tz) => (
                  <button
                    key={tz}
                    onClick={() => setTimezoneMode(tz)}
                    style={{
                      padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                      border: '1.5px solid', borderColor: timezoneMode === tz ? '#1F6C4F' : '#D1D5DB',
                      background: timezoneMode === tz ? '#1F6C4F' : '#fff',
                      color: timezoneMode === tz ? '#fff' : '#374151',
                    }}
                  >
                    {tz === 'user' ? "User's local time" : 'UTC'}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label="Frequency">
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={selectStyle}>
                {FREQUENCY_OPTIONS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </FormField>
            {frequency === 'n_times' && (
              <FormField label="Max impressions">
                <input type="number" value={maxImpressions} onChange={(e) => setMaxImpressions(Number(e.target.value))} style={inputStyle} min={1} />
              </FormField>
            )}
            {frequency === 'until_segment_exits' && (
              <FormField label="Max lifetime impressions per user">
                <input
                  type="number"
                  value={maxLifetimeImpressions ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMaxLifetimeImpressions(v === '' ? null : Math.max(1, Number(v)));
                  }}
                  style={inputStyle}
                  min={1}
                  placeholder="Required"
                />
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                  Required — admin can't activate the campaign without setting this.
                  Caps the lifetime deliveries to any single user. There's no silent
                  default (a runaway loop would over-deliver).
                </div>
              </FormField>
            )}
            {campaignType === 'survey' && (
              <FormField label="Minimum days since last survey">
                <input type="number" value={surveyCooldownDays} onChange={(e) => setSurveyCooldownDays(Number(e.target.value))} style={inputStyle} min={1} />
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Users who opted out permanently will never receive this.</div>
              </FormField>
            )}
            {/* Phase A.1 (2026-05-21): respect_notification_preferences
                — opt-in to user nudge_pref.enabled before delivering push.
                See docs/plans/campaigns_platform_enhancements_sprint.md. */}
            <FormField label="Notification preferences">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={respectNotifPrefs}
                  onChange={(e) => setRespectNotifPrefs(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <div>
                  <div>Respect user notification preferences (push only)</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    When checked, push is skipped for users who have muted Cedar nudges. Chat pill / in-app delivery still happens. Leave unchecked for trial-critical or billing-critical campaigns.
                  </div>
                </div>
              </label>
            </FormField>
          </Section>

          {/* Status Banner */}
          {status === 'active' && (
            <div style={{ marginTop: 24, padding: 12, background: '#D1FAE5', borderRadius: 8, border: '1px solid #065F46', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🟢</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#065F46' }}>
                Campaign is LIVE — {steps[0]?.trigger_type === 'broadcast'
                  ? 'delivering to all matching users on the next 15-minute sweep'
                  : steps[0]?.trigger_type === 'event'
                  ? 'waiting for user actions to trigger delivery'
                  : steps[0]?.trigger_type === 'manual'
                  ? 'waiting for you to press Fire on each step'
                  : 'active and monitoring triggers'}
              </span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button onClick={() => handleSave(false)} disabled={saving} style={{ ...ctaPrimaryStyle, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : status === 'draft' || isNew ? 'Save as Draft' : 'Save Changes'}
            </button>
            {(status === 'draft' || isNew) && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving || !canActivate}
                  title={activationBlockedReason}
                  style={{
                    ...ctaSecondaryStyle,
                    background: !canActivate ? '#6B7280' : '#065F46',
                    color: '#fff',
                    border: 'none',
                    opacity: saving ? 0.6 : !canActivate ? 0.5 : 1,
                    cursor: !canActivate ? 'not-allowed' : saving ? 'wait' : 'pointer',
                  }}
                >
                  {saving ? 'Activating...' : '🚀 Save & Activate'}
                </button>
                {!canActivate && (
                  <div style={{ fontSize: 11, color: '#EF4444', marginTop: 4 }}>
                    {activationBlockedReason}
                  </div>
                )}
              </div>
            )}
            {status === 'active' && (
              <>
                <button onClick={() => handleStatusChange('paused')} style={ctaSecondaryStyle}>Pause</button>
                <button onClick={() => handleStatusChange('completed')} style={ctaSecondaryStyle}>Complete</button>
              </>
            )}
            {status === 'paused' && (
              <button onClick={() => handleStatusChange('active')} style={ctaSecondaryStyle}>Resume</button>
            )}
          </div>
        </div>

        {/* RIGHT: Preview */}
        <div style={{ position: 'sticky', top: 32, alignSelf: 'start' }}>
          <div style={{ padding: 24, background: '#F9FAFB', borderRadius: 16, border: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Preview
            </div>
            <PreviewPanel campaignType={campaignType} steps={steps} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ──

function Section({
  title, children, collapsible = false, defaultOpen = true, summary,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  summary?: string;
}) {
  // 2026-05-22 density pass: marginBottom 32 → 20, header marginBottom
  // 16 → 10. These are the highest-leverage knobs — they compound across
  // every section + field, cutting ~1 full scroll off the New Campaign page
  // without restructuring or hiding anything.
  //
  // 2026-05-22 density lever: `collapsible` sections (Targeting, Scheduling)
  // default collapsed. The `summary` shows the current config in the header
  // while collapsed, so the section is dense without losing discoverability.
  const [open, setOpen] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #E5E7EB' }}>
          {title}
        </h3>
        {children}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          marginBottom: open ? 10 : 0, paddingBottom: 6, borderBottom: '1px solid #E5E7EB',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#111827' }}>
          <span style={{ fontSize: 11, color: '#9CA3AF', width: 10, display: 'inline-block' }}>{open ? '▾' : '▸'}</span>
          {title}
        </span>
        {!open && summary && (
          <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>{summary}</span>
        )}
      </button>
      {open && children}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  // 2026-05-22 density pass: marginBottom 16 → 11, label marginBottom 6 → 4.
  return (
    <div style={{ marginBottom: 11 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function SurveyQuestionsEditor({ questions, onChange, thankYouText, onThankYouChange, introText, onIntroChange }: {
  questions: any[]; onChange: (q: any[]) => void;
  thankYouText: string; onThankYouChange: (t: string) => void;
  introText: string; onIntroChange: (t: string) => void;
}) {
  const addQuestion = () => {
    if (questions.length >= 3) return;
    onChange([...questions, { index: questions.length, type: 'yes_no', text: '', labels: { yes: 'Yes', no: 'No' } }]);
  };

  const updateQuestion = (idx: number, field: string, value: any) => {
    onChange(questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q)));
  };

  return (
    <div style={{ marginTop: 12 }}>
      <FormField label="Consent Intro Text">
        <textarea value={introText} onChange={(e) => onIntroChange(e.target.value)} style={{ ...inputStyle, minHeight: 60 }}
          placeholder="Quick question from the Coach Cedar team" />
      </FormField>
      {questions.map((q, idx) => (
        <div key={idx} style={{ padding: 12, background: '#F9FAFB', borderRadius: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={q.type} onChange={(e) => updateQuestion(idx, 'type', e.target.value)} style={{ ...selectStyle, flex: '0 0 auto', width: 160 }}>
              {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input value={q.text} onChange={(e) => updateQuestion(idx, 'text', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Question text" />
          </div>
          {q.type === 'yes_no' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={q.labels?.yes || ''} onChange={(e) => updateQuestion(idx, 'labels', { ...q.labels, yes: e.target.value })} style={inputStyle} placeholder="Yes label" />
              <input value={q.labels?.no || ''} onChange={(e) => updateQuestion(idx, 'labels', { ...q.labels, no: e.target.value })} style={inputStyle} placeholder="No label" />
            </div>
          )}
          {q.type === 'multiple_choice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {(q.options || [{ label: '', value: '' }]).map((o: any, oi: number) => (
                <input key={oi} value={o.label} onChange={(e) => {
                  const opts = [...(q.options || [])];
                  opts[oi] = { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s/g, '_') };
                  updateQuestion(idx, 'options', opts);
                }} style={inputStyle} placeholder={`Option ${oi + 1}`} />
              ))}
              {(q.options || []).length < 4 && (
                <button onClick={() => updateQuestion(idx, 'options', [...(q.options || []), { label: '', value: '' }])} style={{ ...inputStyle, cursor: 'pointer', color: '#1F6C4F', textAlign: 'center', fontSize: 12 }}>+ Option</button>
              )}
            </div>
          )}
        </div>
      ))}
      {questions.length < 3 && (
        <button onClick={addQuestion} style={{ ...inputStyle, cursor: 'pointer', textAlign: 'center', color: '#1F6C4F', fontWeight: 600, fontSize: 13 }}>
          + Add Question ({questions.length}/3)
        </button>
      )}
      <FormField label="Thank You Text">
        <input value={thankYouText} onChange={(e) => onThankYouChange(e.target.value)} style={inputStyle}
          placeholder="Thanks! This helps us build a better Cedar for you." />
      </FormField>
    </div>
  );
}

// ================================================================
// Design Studio Component
// ================================================================

function DesignStudio({
  campaignType, stepIndex, displayConfig, onUpdate, onApplyPreset,
}: {
  campaignType: string;
  stepIndex: number;
  displayConfig: Record<string, any>;
  onUpdate: (field: string, value: any) => void;
  onApplyPreset: (preset: StylePreset) => void;
}) {
  const presets = getPresetsForType(campaignType);
  const activePresetId = displayConfig.preset_id || '';
  const isCedarChat = campaignType === 'cedar_chat';

  const colorPickerRow = (label: string, field: string, fallback: string) => (
    <FormField label={label}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="color"
          value={displayConfig[field] || fallback}
          onChange={(e) => onUpdate(field, e.target.value)}
          style={{ width: 36, height: 28, border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', padding: 2 }}
        />
        <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'monospace' }}>
          {displayConfig[field] || fallback}
        </span>
      </div>
    </FormField>
  );

  const sliderRow = (label: string, field: string, fallback: number, min: number, max: number, unit: string = 'px') => (
    <FormField label={`${label}: ${displayConfig[field] || fallback}${unit}`}>
      <input
        type="range"
        min={min} max={max}
        value={displayConfig[field] || fallback}
        onChange={(e) => onUpdate(field, Number(e.target.value))}
        style={{ width: '100%', cursor: 'pointer' }}
      />
    </FormField>
  );

  return (
    <div style={{ padding: 14 }}>
      {/* Style Presets — 2026-05-22 density: all presets on ONE row
          (repeat(N,1fr)); compact cards (swatch + label), full description
          moved to a hover tooltip so the grid stays tight. */}
      {presets.length > 1 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Style Presets
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${presets.length}, 1fr)`, gap: 6, marginBottom: 14 }}>
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => onApplyPreset(preset)}
                title={preset.description}
                style={{
                  padding: '8px 8px', borderRadius: 8, border: activePresetId === preset.id ? '2px solid #1F6C4F' : '1px solid #E5E7EB',
                  background: activePresetId === preset.id ? '#E8F2ED' : '#FFFFFF', cursor: 'pointer', textAlign: 'center',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                {/* Mini swatch */}
                <div style={{ display: 'flex', gap: 3, marginBottom: 5, justifyContent: 'center' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: preset.config.background_color || '#FFFFFF', border: '1px solid #E5E7EB' }} />
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: preset.config.accent_color || '#1F6C4F' }} />
                  {preset.config.title_color && <div style={{ width: 14, height: 14, borderRadius: 3, background: preset.config.title_color, border: '1px solid #E5E7EB' }} />}
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#111827', lineHeight: '14px' }}>{preset.label}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Colors — 2026-05-22 density: all pickers on ONE row. */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Colors
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isCedarChat ? 1 : 4}, 1fr)`, gap: 8, marginBottom: 14 }}>
        {!isCedarChat && colorPickerRow('Background', 'background_color', campaignType === 'survey' ? '#E8F2ED' : '#FFFFFF')}
        {colorPickerRow('Accent / CTA', 'accent_color', '#1F6C4F')}
        {!isCedarChat && colorPickerRow('Title', 'title_color', '#111827')}
        {!isCedarChat && colorPickerRow('Body Text', 'body_color', '#6B7280')}
      </div>

      {/* Typography — 2026-05-22 density: 3 sliders on ONE row (3-col grid)
          so the tracks are 1/3 width instead of full-width stacked. */}
      {!isCedarChat && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, marginTop: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Typography
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 6 }}>
            {sliderRow('Title Size', 'title_size', campaignType === 'announcement' ? 28 : 18, 14, 40)}
            {sliderRow('Body Size', 'body_size', campaignType === 'announcement' ? 16 : 14, 12, 24)}
            {sliderRow('Icon Size', 'icon_size', campaignType === 'announcement' ? 48 : 32, 24, 80)}
          </div>
        </>
      )}

      {/* Layout */}
      {!isCedarChat && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, marginTop: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Layout
          </div>
          {campaignType === 'announcement' && (
            <>
              <FormField label="Template">
                <select
                  value={displayConfig.template || 'takeover'}
                  onChange={(e) => onUpdate('template', e.target.value)}
                  style={selectStyle}
                >
                  <option value="takeover">Takeover (Full Screen)</option>
                  <option value="drawer">Drawer (Bottom Sheet — Dismissible)</option>
                  <option value="carousel">Carousel (Swipe Between Beats)</option>
                  <option value="sequence">Sequence (CTA Advances Beats)</option>
                </select>
              </FormField>
              {displayConfig.template === 'drawer' && (
                <FormField label="Drawer Height">
                  <select
                    value={displayConfig.drawer_height || 'auto'}
                    onChange={(e) => onUpdate('drawer_height', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="auto">Auto (fits content)</option>
                    <option value="half">Half Screen</option>
                    <option value="tall">Tall (70%)</option>
                  </select>
                </FormField>
              )}
              {(() => {
                const legacy = displayConfig.content_align || 'center';
                const renderAlignRow = (label: string, field: 'title_align' | 'body_align' | 'cta_align') => {
                  const current = displayConfig[field] || legacy;
                  return (
                    <FormField label={label} key={field}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {(['left', 'center', 'right'] as const).map((align) => (
                          <button
                            key={align}
                            onClick={() => onUpdate(field, align)}
                            style={{
                              flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                              border: current === align ? '2px solid #1F6C4F' : '1px solid #E5E7EB',
                              background: current === align ? '#E8F2ED' : '#FFFFFF',
                              color: current === align ? '#1F6C4F' : '#6B7280',
                            }}
                            title={`${label} ${align}`}
                          >
                            {align === 'left' ? '⇤' : align === 'center' ? '⇔' : '⇥'}
                          </button>
                        ))}
                      </div>
                    </FormField>
                  );
                };
                // 2026-05-22 density: the 3 alignment controls sit side by
                // side on ONE row (3-col grid) instead of stacked. A shared
                // "Alignment" label keeps the shortened Title/Body/Button
                // sub-labels legible.
                return (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                      Alignment
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      {renderAlignRow('Title', 'title_align')}
                      {renderAlignRow('Body', 'body_align')}
                      {renderAlignRow('Button', 'cta_align')}
                    </div>
                  </div>
                );
              })()}
              {sliderRow('Button Radius', 'border_radius', 12, 0, 24)}
            </>
          )}
          {campaignType === 'contextual_notice' && (
            <FormField label="Sheet Height">
              <select
                value={displayConfig.height || 'auto'}
                onChange={(e) => onUpdate('height', e.target.value)}
                style={selectStyle}
              >
                <option value="auto">Auto (fits content)</option>
                <option value="half">Half Screen</option>
                <option value="tall">Tall (70%)</option>
              </select>
            </FormField>
          )}
          {campaignType === 'survey' && (
            <>
              {colorPickerRow('Team Badge', 'team_badge_color', '#1F6C4F')}
            </>
          )}
        </>
      )}

      {/* Dismiss Behavior */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10, marginTop: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Dismiss Behavior
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
          <input
            type="radio"
            name={`dismiss-${stepIndex}`}
            checked={(displayConfig.dismiss_behavior || 'x_and_cta') === 'x_and_cta'}
            onChange={() => onUpdate('dismiss_behavior', 'x_and_cta')}
          />
          X button + CTA
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
          <input
            type="radio"
            name={`dismiss-${stepIndex}`}
            checked={displayConfig.dismiss_behavior === 'cta_only'}
            onChange={() => onUpdate('dismiss_behavior', 'cta_only')}
          />
          CTA only (must engage)
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Slides editor — beats for carousel + sequence announcement templates.
// `primary` mirrors the step's top-level title/body/icon so we can seed Beat 1
// from existing content when the admin first switches into a multi-beat
// template. Once slides[] is populated it becomes the source of truth for
// carousel/sequence rendering (see CampaignAnnouncementModal.native.tsx).
// ─────────────────────────────────────────────────────────────────────────────
function SlidesEditor({
  template,
  slides,
  primary,
  onChange,
}: {
  template: string;
  slides: Array<{ title?: string; body?: string; icon?: string; cta_label?: string }>;
  primary: { title: string; body: string; icon: string };
  onChange: (next: Array<{ title?: string; body?: string; icon?: string; cta_label?: string }>) => void;
}) {
  if (template === 'takeover') return null;

  const isSequence = template === 'sequence';
  const updateSlide = (i: number, field: string, value: any) => {
    onChange(slides.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };
  const removeSlide = (i: number) => onChange(slides.filter((_, idx) => idx !== i));
  const moveSlide = (i: number, dir: -1 | 1) => {
    const next = [...slides];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const addSlide = () => {
    const next = [...slides];
    if (next.length === 0) {
      next.push({ title: primary.title, body: primary.body, icon: primary.icon });
      next.push({ title: '', body: '', icon: '' });
    } else {
      next.push({ title: '', body: '', icon: '' });
    }
    onChange(next);
  };

  return (
    <div style={{ marginTop: 16, padding: 12, border: '1px solid #E5E7EB', borderRadius: 10, background: '#FAFAFA' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {isSequence ? 'Sequence Beats' : 'Carousel Slides'}
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
          {slides.length === 0
            ? 'Beat 1 uses the Title/Body above'
            : `${slides.length} beat${slides.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {slides.length === 0 && (
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10, lineHeight: '18px' }}>
          {isSequence
            ? 'Add beats to walk the user through multiple screens. Each beat has its own CTA label; the last beat dismisses.'
            : 'Add slides to make this a swipeable multi-card announcement.'}
        </div>
      )}

      {slides.map((slide, i) => (
        <div key={i} style={{ padding: 10, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              Beat {i + 1}{isSequence && i === slides.length - 1 ? ' (final — dismisses)' : ''}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => moveSlide(i, -1)} disabled={i === 0} style={miniBtn(i === 0)} title="Move up">↑</button>
              <button onClick={() => moveSlide(i, 1)} disabled={i === slides.length - 1} style={miniBtn(i === slides.length - 1)} title="Move down">↓</button>
              <button onClick={() => removeSlide(i)} style={{ ...miniBtn(false), color: '#EF4444' }} title="Remove">✕</button>
            </div>
          </div>
          <input
            value={slide.title || ''}
            onChange={(e) => updateSlide(i, 'title', e.target.value)}
            placeholder="Title"
            style={{ ...inputStyle, marginBottom: 6 }}
          />
          <textarea
            value={slide.body || ''}
            onChange={(e) => updateSlide(i, 'body', e.target.value)}
            placeholder="Body (use Enter for line breaks)"
            style={{ ...inputStyle, minHeight: 60, marginBottom: 6 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: isSequence ? '1fr 1fr' : '1fr', gap: 6 }}>
            <select
              value={slide.icon || ''}
              onChange={(e) => updateSlide(i, 'icon', e.target.value)}
              style={selectStyle}
            >
              <option value="">No icon</option>
              <option value="watch">Watch</option>
              <option value="rest">Rest</option>
              <option value="fire">Fire</option>
              <option value="star">Star</option>
              <option value="clock">Clock</option>
              <option value="check">Check</option>
              <option value="gift">Gift</option>
              <option value="megaphone">Megaphone</option>
            </select>
            {isSequence && (
              <input
                value={slide.cta_label || ''}
                onChange={(e) => updateSlide(i, 'cta_label', e.target.value)}
                placeholder={i === slides.length - 1 ? 'Final CTA (e.g. Done)' : 'Next CTA (e.g. Tell me more)'}
                style={inputStyle}
              />
            )}
          </div>
        </div>
      ))}

      <button
        onClick={addSlide}
        style={{
          width: '100%', padding: '8px 12px', background: '#FFFFFF', border: '1px dashed #D1D5DB',
          borderRadius: 8, fontSize: 12, color: '#6B7280', cursor: 'pointer',
        }}
      >
        {slides.length === 0 ? '+ Seed from primary + add second beat' : '+ Add another beat'}
      </button>
    </div>
  );
}

const miniBtn = (disabled: boolean): React.CSSProperties => ({
  background: '#F3F4F6',
  border: '1px solid #E5E7EB',
  borderRadius: 6,
  padding: '2px 8px',
  fontSize: 11,
  color: disabled ? '#D1D5DB' : '#6B7280',
  cursor: disabled ? 'default' : 'pointer',
});

const FILTER_FIELDS = [
  { value: 'subscription_tier', label: 'Subscription Tier', operators: ['eq', 'in'], type: 'select', options: ['trial', 'full', 'extended'] },
  // Phase A.2 (2026-05-21): cohort filter using the derived cohort
  // from services/subscription_cohort.py. Same source of truth used
  // by PaywallModal + /api/user/subscription-status — replaces the
  // workaround of stitching subscription_tier + is_churned + trial_day.
  // All 7 cohort values exposed; admins typically use trial_active,
  // subscription_expired, paid_active, paid_cancelled_grace.
  { value: 'subscription_cohort', label: 'Subscription Cohort', operators: ['eq', 'in'], type: 'select', options: ['pre_trial', 'trial_active', 'subscription_expired', 'paid_active', 'paid_cancelled_grace', 'billing_issue', 'unknown'] },
  { value: 'platform', label: 'Platform', operators: ['eq', 'in'], type: 'select', options: ['ios', 'android', 'pwa'] },
  { value: 'account_age_days', label: 'Account Age (days)', operators: ['gte', 'lte', 'between'], type: 'number' },
  { value: 'trial_day', label: 'Trial Day', operators: ['eq', 'gte', 'lte'], type: 'number' },
  { value: 'activity_level', label: 'Activity Level', operators: ['eq', 'in'], type: 'select', options: ['daily_active', 'weekly_active', 'lapsed', 'dormant'] },
  { value: 'goals_created', label: 'Goals Created', operators: ['gte', 'lte'], type: 'number' },
  { value: 'workouts_completed', label: 'Workouts Completed', operators: ['gte', 'lte'], type: 'number' },
  { value: 'has_active_plan', label: 'Has Active Plan', operators: ['eq'], type: 'boolean' },
  { value: 'has_connected_device', label: 'Has Connected Device', operators: ['eq'], type: 'select', options: ['garmin', 'apple_watch', 'health_connect'] },
  { value: 'push_notifications_enabled', label: 'Push Enabled', operators: ['eq'], type: 'boolean' },
  { value: 'is_churned', label: 'Is Churned', operators: ['eq'], type: 'boolean' },
];

function FilterBuilder({ filters, onChange, label }: {
  filters: Array<{ field: string; operator: string; value: any }>;
  onChange: (f: Array<{ field: string; operator: string; value: any }>) => void;
  label: string;
}) {
  const addFilter = () => onChange([...filters, { field: 'subscription_tier', operator: 'eq', value: '' }]);
  const removeFilter = (idx: number) => onChange(filters.filter((_, i) => i !== idx));
  const updateFilter = (idx: number, key: string, val: any) => onChange(filters.map((f, i) => i === idx ? { ...f, [key]: val } : f));

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>{label}</div>
      {filters.map((f, idx) => {
        const fieldDef = FILTER_FIELDS.find((fd) => fd.value === f.field);
        return (
          <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <select value={f.field} onChange={(e) => updateFilter(idx, 'field', e.target.value)} style={{ ...selectStyle, flex: '0 0 180px' }}>
              {FILTER_FIELDS.map((fd) => <option key={fd.value} value={fd.value}>{fd.label}</option>)}
            </select>
            <select value={f.operator} onChange={(e) => updateFilter(idx, 'operator', e.target.value)} style={{ ...selectStyle, flex: '0 0 80px' }}>
              {(fieldDef?.operators || ['eq']).map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
            {fieldDef?.type === 'select' ? (
              <select value={f.value} onChange={(e) => updateFilter(idx, 'value', e.target.value)} style={{ ...selectStyle, flex: 1 }}>
                <option value="">Select...</option>
                {fieldDef.options?.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : fieldDef?.type === 'boolean' ? (
              <select value={String(f.value)} onChange={(e) => updateFilter(idx, 'value', e.target.value === 'true')} style={{ ...selectStyle, flex: 1 }}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input type="number" value={f.value || ''} onChange={(e) => updateFilter(idx, 'value', Number(e.target.value))} style={{ ...inputStyle, flex: 1 }} />
            )}
            <button onClick={() => removeFilter(idx)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, padding: 4 }}>✕</button>
          </div>
        );
      })}
      <button onClick={addFilter} style={{ background: 'none', border: '1px dashed #D1D5DB', borderRadius: 6, padding: '6px 12px', color: '#6B7280', fontSize: 12, cursor: 'pointer', width: '100%' }}>
        + Add filter
      </button>
    </div>
  );
}

function PreviewPanel({ campaignType, steps }: { campaignType: string; steps: StepData[] }) {
  const step = steps[0];
  const [activeSlide, setActiveSlide] = useState(0);

  // Reset active slide when the step's slides change shape
  const slidesKey = step ? JSON.stringify((step.content?.slides || []).map((s: any) => s?.title || '')) : '';
  React.useEffect(() => { setActiveSlide(0); }, [slidesKey]);

  if (!step) return <div style={{ color: '#9CA3AF', fontSize: 14 }}>Add a step to see preview</div>;

  const content = step.content;
  const dc = step.display_config || {};
  const accentColor = dc.accent_color || '#1F6C4F';
  const bgColor = dc.background_color || (campaignType === 'survey' ? '#E8F2ED' : '#FFFFFF');
  // Gradient (Cedar Gradient preset + any future gradient preset). When
  // present, the renderer wraps the screen in a linear-gradient; the
  // CSS `background` property accepts a `linear-gradient(...)` string,
  // so we synthesize one for the preview pane here. Native render uses
  // expo-linear-gradient (see CampaignAnnouncementModal.native.tsx).
  const bgGradient = dc.background_gradient as
    | { colors: string[]; locations?: number[]; start?: { x: number; y: number }; end?: { x: number; y: number } }
    | undefined;
  const bgGradientCss = bgGradient
    ? (() => {
        // Default: horizontal left→right. start/end are 0–1 unit-square
        // coordinates from expo-linear-gradient; convert to CSS angle.
        const dx = (bgGradient.end?.x ?? 1) - (bgGradient.start?.x ?? 0);
        const dy = (bgGradient.end?.y ?? 0) - (bgGradient.start?.y ?? 0);
        // CSS angle: 0deg = up; we want 90deg = right for the default.
        const angleDeg = Math.round((Math.atan2(dx, -dy) * 180) / Math.PI);
        const stops = bgGradient.colors.map((c, i) => {
          const loc = bgGradient.locations?.[i];
          return loc != null ? `${c} ${Math.round(loc * 100)}%` : c;
        });
        return `linear-gradient(${angleDeg}deg, ${stops.join(', ')})`;
      })()
    : null;
  const titleColor = dc.title_color || '#111827';
  const bodyColor = dc.body_color || '#6B7280';
  const titleSize = dc.title_size || (campaignType === 'announcement' ? 28 : 18);
  const bodySize = dc.body_size || (campaignType === 'announcement' ? 16 : 14);
  const iconSize = dc.icon_size || (campaignType === 'announcement' ? 48 : 32);
  const legacyAlign = (dc.content_align || 'center') as 'left' | 'center' | 'right';
  const titleAlign = (dc.title_align || legacyAlign) as 'left' | 'center' | 'right';
  const bodyAlign = (dc.body_align || legacyAlign) as 'left' | 'center' | 'right';
  const ctaAlign = (dc.cta_align || legacyAlign) as 'left' | 'center' | 'right';
  // Kept for non-announcement preview branches that still use a single alignment.
  const contentAlign = legacyAlign;
  const borderRadius = dc.border_radius ?? 12;
  const teamBadgeColor = dc.team_badge_color || '#1F6C4F';
  const showX = (dc.dismiss_behavior || 'x_and_cta') === 'x_and_cta';
  // Theme-aware X close button (Mission 10 + sprint 2026-05-21).
  // Dark background OR gradient → light X on translucent-white circle.
  // Light background → dark X on soft-gray circle (Mission 10 standard
  // from BottomSheet.tsx).
  const isDarkBg = !!bgGradient || (() => {
    const h = bgColor.replace(/^#/, '');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return false;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b < 128;
  })();
  const xBgColor = isDarkBg ? 'rgba(255,255,255,0.15)' : '#F3F3F3';
  const xFgColor = isDarkBg ? 'rgba(255,255,255,0.80)' : '#6B7280';

  const template = (dc.template || 'takeover') as 'takeover' | 'drawer' | 'carousel' | 'sequence';
  const slides: Array<{ title?: string; body?: string; icon?: string; cta_label?: string }> = content.slides || content.carousel_slides || [];
  const hasSlides = slides.length > 1;
  const isCarouselPreview = template === 'carousel' && hasSlides;
  const isSequencePreview = template === 'sequence' && hasSlides;
  // Drawer template renders as a bottom sheet, mirroring the native
  // BottomSheet wrapper (CampaignAnnouncementModal.native.tsx). Native
  // already renders this correctly; the preview previously fell through
  // to the full-screen takeover, so "Drawer" looked identical to
  // "Takeover" in the editor. drawer_height maps to a minHeight fraction
  // of the phone screen.
  const isDrawerPreview = template === 'drawer';
  const drawerHeight = (dc.drawer_height || 'auto') as 'auto' | 'half' | 'tall';
  const currentSlide = (isCarouselPreview || isSequencePreview) ? slides[Math.min(activeSlide, slides.length - 1)] : null;
  const previewTitle = currentSlide?.title ?? content.title;
  const previewBody = currentSlide?.body ?? content.body;
  const previewIcon = currentSlide?.icon ?? content.icon;
  const sequenceCtaLabel = isSequencePreview
    ? (currentSlide?.cta_label || content.cta_primary?.label || (activeSlide === slides.length - 1 ? 'Done' : 'Next'))
    : null;
  const ctaJustify: 'flex-start' | 'center' | 'flex-end' = ctaAlign === 'left' ? 'flex-start' : ctaAlign === 'right' ? 'flex-end' : 'center';

  return (
    <div style={{
      background: '#1F2937', borderRadius: 44, padding: 12, maxWidth: 300, margin: '0 auto',
      boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    }}>
      {/* Notch */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
        <div style={{ width: 120, height: 28, background: '#111827', borderRadius: 14 }} />
      </div>
      {/* Status bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '2px 20px 6px', color: '#9CA3AF', fontSize: 11, fontWeight: 600,
      }}>
        <span>9:41</span>
        <span style={{ fontSize: 10, letterSpacing: 1 }}>.... wifi bat</span>
      </div>
      {/* Screen area */}
      <div style={{
        background: bgGradientCss || bgColor, borderRadius: 36, minHeight: 540, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', position: 'relative',
      }}>
        {/* -- Announcement preview -- */}
        {campaignType === 'announcement' && (() => {
          // Shared inner content (icon -> title -> body -> slide dots -> CTA),
          // rendered identically inside the full-screen takeover container and
          // the drawer bottom-sheet container. The close affordance differs per
          // container (takeover: floating X; drawer: grab handle + X on the
          // sheet, matching the native BottomSheet), so each container renders
          // it rather than this shared block.
          const announcementInner = (
            <>
              <div style={{
                marginBottom: 24,
                display: 'flex',
                justifyContent: titleAlign === 'left' ? 'flex-start' : titleAlign === 'right' ? 'flex-end' : 'center',
              }}>
                <CampaignIcon name={previewIcon} size={Math.round(iconSize * 0.72)} color={accentColor} />
              </div>
              <div style={{
                fontSize: Math.round(titleSize * 0.64), fontWeight: 700, color: titleColor,
                textAlign: titleAlign as any, marginBottom: 8, lineHeight: `${Math.round(titleSize * 0.86)}px`,
                whiteSpace: 'pre-wrap',
              }}>
                {previewTitle || 'Announcement Title'}
              </div>
              <div style={{
                fontSize: Math.round(bodySize * 0.76), color: bodyColor,
                textAlign: bodyAlign as any, lineHeight: `${Math.round(bodySize * 1.18)}px`,
                whiteSpace: 'pre-wrap',
              }}>
                {previewBody || 'Body text appears here. Describe what is new or important.'}
              </div>
              {(isCarouselPreview || isSequencePreview) && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
                  {slides.map((_, i) => (
                    <div
                      key={i}
                      onClick={() => isCarouselPreview && setActiveSlide(i)}
                      style={{
                        width: activeSlide === i ? 18 : 6, height: 6, borderRadius: 3,
                        background: activeSlide === i ? accentColor : '#D1D5DB',
                        cursor: isCarouselPreview ? 'pointer' : 'default',
                        transition: 'width 120ms ease',
                      }}
                    />
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: ctaJustify, marginTop: 24 }}>
                <div
                  onClick={() => {
                    if (!isSequencePreview) return;
                    if (activeSlide < slides.length - 1) setActiveSlide(activeSlide + 1);
                    else setActiveSlide(0);
                  }}
                  style={{
                    // Login-parity CTA (LoginScreen.native.tsx signInButton):
                    // full-width when alignment is center, content-width otherwise.
                    // 2026-05-22: vertical padding trimmed 12 → 9 to mirror the
                    // native CTA slim-down (paddingVertical 16 → 13) at preview scale.
                    background: accentColor, color: '#fff',
                    padding: '9px 32px',
                    width: ctaAlign === 'center' ? '100%' : 'auto',
                    textAlign: 'center',
                    borderRadius: Math.round(borderRadius * 0.86),
                    fontSize: 14, fontWeight: 600, letterSpacing: 0.3,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.20)',
                    cursor: isSequencePreview ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  {isSequencePreview
                    ? sequenceCtaLabel
                    : (content.cta_primary?.label || 'Learn More')}
                </div>
              </div>
            </>
          );

          const closeX = showX ? (
            <div style={{
              position: 'absolute', top: 16, right: 16, width: 32, height: 32,
              borderRadius: 16, background: xBgColor, display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: xFgColor,
              cursor: 'default', zIndex: 2,
            }}>
              <X size={16} color={xFgColor} strokeWidth={1.5} />
            </div>
          ) : null;

          if (isDrawerPreview) {
            // Bottom sheet: dimmed backdrop above, content-carrying sheet
            // anchored at the bottom with a grab handle. minHeight tracks
            // drawer_height (auto fits content; half ~= 50%; tall ~= 70% of
            // the 540px preview screen).
            const sheetMinHeight =
              drawerHeight === 'tall' ? 378 : drawerHeight === 'half' ? 270 : undefined;
            return (
              // 2026-05-22: neutral scrim behind the sheet (matches the
              // contextual notice fix) so the rounded corners reveal a clean
              // dimmed-app backdrop, not the screen-area background_color.
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', background: '#4B5563' }}>
                {/* Dimmed app area above the sheet */}
                <div style={{ flex: 1 }} />
                {/* Bottom sheet — corner radius 20 matches Mission 10
                    BottomSheet.tsx (borderTopLeftRadius: 20). The native
                    drawer wraps the real <BottomSheet>, so this keeps the
                    preview faithful to the in-app drawer. */}
                <div style={{
                  background: bgGradientCss || bgColor,
                  borderTopLeftRadius: 20, borderTopRightRadius: 20,
                  padding: '12px 28px 28px', position: 'relative',
                  minHeight: sheetMinHeight,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}>
                  {/* Grab handle (Mission 10 BottomSheet) */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <div style={{
                      width: 40, height: 4, borderRadius: 2,
                      background: isDarkBg ? 'rgba(255,255,255,0.30)' : '#D1D5DB',
                    }} />
                  </div>
                  {closeX}
                  {announcementInner}
                </div>
              </div>
            );
          }

          // Takeover (and carousel/sequence): full-screen centered content.
          return (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
              padding: 28, position: 'relative',
            }}>
              {closeX}
              {announcementInner}
            </div>
          );
        })()}
        {/* -- Contextual Notice preview (bottom sheet) -- */}
        {campaignType === 'contextual_notice' && (
          // 2026-05-22: a contextual notice OVERLAYS the app — the area
          // behind/around the sheet's rounded corners is a neutral dimmed
          // scrim, NOT the campaign's background_color. Previously this
          // container inherited the (possibly dark) screen-area bgColor,
          // so the white sheet's rounded corners revealed dark-cedar
          // triangles. A solid neutral scrim fixes that.
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', background: '#4B5563' }}>
            {/* Dimmed app area above the sheet */}
            <div style={{ flex: 1 }} />
            {/* Bottom sheet — honors the configured background_color so the
                Cedar Classic (dark) preset renders dark here exactly as it
                does on device (CampaignContextualNotice passes the same color
                as the BottomSheet headerBgColor). */}
            <div style={{
              background: bgColor, borderTopLeftRadius: 20, borderTopRightRadius: 20,
              padding: '12px 24px 28px', position: 'relative',
            }}>
              {/* Handle — theme-aware (white-translucent on a dark sheet, matching BottomSheet) */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <div style={{ width: 40, height: 4, background: isDarkBg ? 'rgba(255,255,255,0.5)' : '#D1D5DB', borderRadius: 2 }} />
              </div>
              {showX && (
                <div style={{
                  position: 'absolute', top: 12, right: 16, width: 26, height: 26,
                  borderRadius: 13, background: isDarkBg ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 13, color: isDarkBg ? 'rgba(255,255,255,0.85)' : '#6B7280',
                }}>
                  x
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <CampaignIcon name={content.icon} size={Math.round(iconSize * 0.72)} color={accentColor} />
              </div>
              <div style={{
                fontSize: Math.round(titleSize * 0.88), fontWeight: 600, color: titleColor, textAlign: 'center', marginBottom: 6,
              }}>
                {content.title || 'Notice Title'}
              </div>
              <div style={{
                fontSize: Math.round(bodySize * 0.93), color: bodyColor, textAlign: 'center', lineHeight: '19px', marginBottom: 16,
              }}>
                {content.body || 'Notice body text goes here.'}
              </div>
              <div style={{
                background: accentColor, color: '#fff', padding: '11px 0', borderRadius: 10,
                fontSize: 14, fontWeight: 600, textAlign: 'center',
              }}>
                {content.cta_primary?.label || 'Got It'}
              </div>
            </div>
          </div>
        )}

        {/* -- Cedar Chat preview -- */}
        {campaignType === 'cedar_chat' && (
          <div style={{
            flex: 1, background: '#F9FAFB', display: 'flex', flexDirection: 'column',
            justifyContent: 'flex-end', padding: '20px 16px 24px',
          }}>
            {/* Fake earlier messages */}
            <div style={{
              alignSelf: 'flex-end', background: '#E8F2ED', borderRadius: 16,
              borderBottomRightRadius: 4, padding: '10px 14px', fontSize: 12, color: '#374151',
              maxWidth: '70%', marginBottom: 16, lineHeight: '17px',
            }}>
              How should I warm up today?
            </div>
            {/* Cedar message */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14, background: accentColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#fff', fontWeight: 700, flexShrink: 0,
              }}>
                CC
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: accentColor, fontWeight: 600, marginBottom: 3 }}>Cedar</div>
                <div style={{
                  background: '#fff', borderRadius: 16, borderBottomLeftRadius: 4,
                  padding: '12px 14px', fontSize: 13, color: '#374151', lineHeight: '19px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  {content.message || 'Cedar campaign message preview...'}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#D97706', marginTop: 10, background: '#FFFBEB', padding: '5px 8px', borderRadius: 6, textAlign: 'center' }}>
              Essentials users can read but responding counts against limit
            </div>
          </div>
        )}

        {/* -- Survey consent preview -- */}
        {campaignType === 'survey' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Top portion with Cedar branding */}
            <div style={{
              background: bgColor, flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: '32px 24px 20px',
            }}>
              {/* Coach Cedar team badge \u2014 CC monogram (replaces the old \uD83C\uDF32). */}
              <div style={{ marginBottom: 10 }}>
                <CedarTeamBadge size={48} color={teamBadgeColor} />
              </div>
              <div style={{
                fontSize: 12, fontWeight: 700, color: teamBadgeColor, textTransform: 'uppercase',
                letterSpacing: 1.2, marginBottom: 14,
              }}>
                Coach Cedar Team
              </div>
              <div style={{
                fontSize: 14, color: '#374151', textAlign: 'center', lineHeight: '20px',
                maxWidth: 200,
              }}>
                {content.intro_text || 'Quick question from the Coach Cedar team'}
              </div>
            </div>
            {/* Buttons */}
            <div style={{ padding: '0 24px 32px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{
                background: accentColor, color: '#fff', padding: '12px 0', borderRadius: 10,
                fontSize: 14, fontWeight: 600, textAlign: 'center',
              }}>
                Sure!
              </div>
              <div style={{
                background: '#F3F4F6', color: '#374151', padding: '12px 0', borderRadius: 10,
                fontSize: 14, fontWeight: 500, textAlign: 'center',
              }}>
                Not Now
              </div>
              <div style={{
                background: 'transparent', color: '#9CA3AF', padding: '10px 0',
                fontSize: 12, textAlign: 'center',
              }}>
                Don't ask me again
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Home indicator */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8, paddingBottom: 4 }}>
        <div style={{ width: 134, height: 5, background: '#D1D5DB', borderRadius: 3 }} />
      </div>
    </div>
  );
}

function createEmptyStep(type: string): StepData {
  return {
    trigger_type: 'broadcast',
    trigger_ids: [],
    trigger_config: {},
    requires_previous_step: false,
    message_type: type,
    content: {},
    display_config: {},
    send_push: false,
    push_title: '',
    push_body: '',
    // Phase C: all per-step scheduling overrides default to disabled (null).
    delay_after_previous_minutes: null,
    send_at_local_time: null,
    send_window_minutes: null,
    quiet_hours_start: null,
    quiet_hours_end: null,
    // Phase D: no A/B variants by default — step delivers from step.content.
    variants: [],
  };
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#F3F4F6', text: '#374151' },
  active: { bg: '#D1FAE5', text: '#065F46' },
  paused: { bg: '#FEF3C7', text: '#92400E' },
  completed: { bg: '#DBEAFE', text: '#1E40AF' },
};

const inputStyle: React.CSSProperties = {
  // 2026-05-22 density pass: vertical padding 10 → 8 (compounds across
  // every text input / select / textarea on the page).
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1.5px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle, background: '#fff',
};

const backLinkStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#6B7280',
  fontSize: 13, cursor: 'pointer', padding: 0,
};

const ctaPrimaryStyle: React.CSSProperties = {
  padding: '12px 24px', borderRadius: 10, border: 'none',
  background: '#1F6C4F', color: '#fff', fontSize: 14,
  fontWeight: 600, cursor: 'pointer',
};

const ctaSecondaryStyle: React.CSSProperties = {
  padding: '12px 24px', borderRadius: 10, border: '1.5px solid #d1d5db',
  background: '#fff', color: '#374151', fontSize: 14,
  fontWeight: 600, cursor: 'pointer',
};
